/**
 * Patch Fetch Strategy
 * Copies value from another field in the same form
 */

import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { BaseDataFetchStrategy } from './data-fetch-strategy.interface';
import { IDataFetchContext } from '../models';
import { DataFetchType } from '../constants';

@Injectable({ providedIn: 'root' })
export class PatchFetchStrategy extends BaseDataFetchStrategy {
  canHandle(dataFetchType: string): boolean {
    return dataFetchType?.toLowerCase() === DataFetchType.Patch;
  }
  
  getName(): string {
    return 'Patch Strategy';
  }
  
  override validate(context: IDataFetchContext): boolean {
    if (!super.validate(context)) {
      return false;
    }
    
    if (!context.field.patchValueFrom) {
      this.logWarning('No patchValueFrom field specified', context);
      return false;
    }
    
    // Check if source field exists
    const sourceControl = context.formGroup.get(context.field.patchValueFrom);
    if (!sourceControl) {
      this.logWarning(`Source field "${context.field.patchValueFrom}" not found in form`, context);
      return false;
    }
    
    return true;
  }
  
  execute(context: IDataFetchContext): Observable<any> {
    if (!this.validate(context)) {
      return of(null);
    }
    
    try {
      // Get value from source field
      const sourceControl = context.formGroup.get(context.field.patchValueFrom!);
      const value = sourceControl?.value;
      
      // If no value, return null
      if (value === null || value === undefined) {
        return of(null);
      }
      
      // If patchPropertyName is specified, extract that property
      if (context.field.patchPropertyName && typeof value === 'object') {
        const extractedValue = value[context.field.patchPropertyName];
        this.logInfo(`Patched value from ${context.field.patchValueFrom}.${context.field.patchPropertyName}`, context);
        return of(extractedValue);
      }
      
      // Otherwise return the whole value
      this.logInfo(`Patched value from ${context.field.patchValueFrom}`, context);
      return of(value);
      
    } catch (error) {
      this.logError(error, context);
      return of(null);
    }
  }
  
  setup(context: IDataFetchContext): void {
    if (!this.validate(context)) {
      return;
    }
    
    // Subscribe to source field changes
    const sourceControl = context.formGroup.get(context.field.patchValueFrom!);
    if (!sourceControl) return;
    
    sourceControl.valueChanges.subscribe((value) => {
      if (!context.field.dbFeildName) return;
      
      let patchedValue = value;
      
      // Extract property if specified
      if (context.field.patchPropertyName && value && typeof value === 'object') {
        patchedValue = value[context.field.patchPropertyName];
      }
      
      // Set value in target field
      const targetControl = context.formGroup.get(context.field.dbFeildName);
      if (targetControl) {
        targetControl.setValue(patchedValue, { emitEvent: false });
        this.logInfo(`Auto-patched value: ${patchedValue}`, context);
      }
    });
  }
}
